import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Input, Select, Stack, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { operations } from 'api/schema';
import { dropzone } from 'helpers/makeFormFields';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useCreateLegalDocumentMutation } from 'hooks/mutations/useCreateLegalDocumentMutation';
import useFileUploadMutation from 'hooks/mutations/useFileUploadMutation';
import { ContentCard } from 'layout/ContentCard';
import PageMetatags from 'layout/PageMetatags';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

export type UploadedFileData = operations['fileUpload']['responses']['201']['content']['application/json']['data'];

const CreateLegalDocumentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const schema = yup
    .object({
      version: yup.string().required(),
      type: yup.string().oneOf(['dpp', 'tos']).required(),
      effective_at_month: yup.string().required(),
      effective_at_year: yup.number().required(),
      en_file: yup.mixed(),
      de_file: yup.mixed(),
      de_file_uuid: yup.string().required(),
      en_file_uuid: yup.string().required(),
    })
    .required();

  const fileUploadMutation = useFileUploadMutation();
  const storeLegalDocumentVersions = useCreateLegalDocumentMutation();

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      de_file_uuid: '',
      en_file_uuid: '',
      effective_at_month: '',
      effective_at_year: new Date().getFullYear(),
      version: '',
    },
  });

  const fileENHookForm = watch('en_file');
  const fileDEHookForm = watch('de_file');

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    const month = data.effective_at_month;
    const year = data.effective_at_year;
    const dateToSubmit = `${year}-${month}`;

    return storeLegalDocumentVersions.mutate(
      {
        version: data.version,
        effective_at: dateToSubmit,
        de_file_uuid: data.de_file_uuid,
        en_file_uuid: data.en_file_uuid,
        type: data.type,
      },
      {
        onSuccess: () => {
          navigate('/app/op/legal-documents', { replace: true });
        },
        onError: (error: any) => {
          const { message } = error.response.data;
          manageErrorResponse(toast, message);
        },
      },
    );
  };

  useEffect(() => {
    if (fileENHookForm) {
      const formData = new FormData();
      formData.append('file', fileENHookForm.file as File);

      fileUploadMutation.mutate(formData, {
        onSuccess: (fileData) => setValue('en_file_uuid', fileData.data.uuid),
        onError: (error: any) => {
          const { errors } = error.response.data;
          manageErrorResponse(toast, errors);
        },
      });
    } else {
      setValue('en_file_uuid', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileENHookForm]);

  useEffect(() => {
    if (fileDEHookForm) {
      const formData = new FormData();
      formData.append('file', fileDEHookForm.file as File);

      fileUploadMutation.mutate(formData, {
        onSuccess: (fileData) => setValue('de_file_uuid', fileData.data.uuid),
        onError: (error: any) => {
          const { errors } = error.response.data;
          manageErrorResponse(toast, errors);
        },
      });
    } else {
      setValue('de_file_uuid', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileDEHookForm]);

  return (
    <Box>
      <PageMetatags title="Create legal document" />
      <ContentCard header={t('legal_documents:create_legal_document.title')}>
        <Stack as="form" spacing={8} p={1} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name={'type'}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid}>
                <FormLabel>{t('legal_documents:legal_document_properties.type')}</FormLabel>
                <Select placeholder={t('legal_documents:legal_document_properties.types.placeholder')} {...field}>
                  <option value="tos">{t('legal_documents:legal_document_properties.types.tos')}</option>
                  <option value="dpp">{t('legal_documents:legal_document_properties.types.dpp')}</option>
                </Select>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
          <Controller
            name={'version'}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl isInvalid={fieldState.invalid}>
                <FormLabel>{t('legal_documents:legal_document_properties.version')}</FormLabel>
                <Input {...field} placeholder="1.0"></Input>
                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
          <FormControl>
            <HStack>
              <Controller
                name={'effective_at_month'}
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={fieldState.invalid}>
                    <FormLabel>{t('legal_documents:month_dropdown.month')}</FormLabel>
                    <Select placeholder={t('legal_documents:month_dropdown.placeholder')} {...field}>
                      <option value="01">{t('legal_documents:month_dropdown.jan')}</option>
                      <option value="02">{t('legal_documents:month_dropdown.feb')}</option>
                      <option value="03">{t('legal_documents:month_dropdown.mar')}</option>
                      <option value="04">{t('legal_documents:month_dropdown.apr')}</option>
                      <option value="05">{t('legal_documents:month_dropdown.may')}</option>
                      <option value="06">{t('legal_documents:month_dropdown.jun')}</option>
                      <option value="07">{t('legal_documents:month_dropdown.jul')}</option>
                      <option value="08">{t('legal_documents:month_dropdown.aug')}</option>
                      <option value="09">{t('legal_documents:month_dropdown.sep')}</option>
                      <option value="10">{t('legal_documents:month_dropdown.oct')}</option>
                      <option value="11">{t('legal_documents:month_dropdown.nov')}</option>
                      <option value="12">{t('legal_documents:month_dropdown.dec')}</option>
                    </Select>
                  </FormControl>
                )}
              ></Controller>
              <Controller
                name={'effective_at_year'}
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={fieldState.invalid}>
                    <FormLabel>{t('legal_documents:year')}</FormLabel>
                    <Input placeholder="Enter year" {...field} />
                  </FormControl>
                )}
              ></Controller>
            </HStack>
          </FormControl>
          {dropzone({
            name: 'en_file',
            // @ts-ignore
            fontSize: '16px',
            required: true,
            // @ts-ignore
            label: <FormLabel>{t('legal_documents:legal_document_properties.versions.en')}</FormLabel>,
            control,
            register,
            errors,
          })}
          {dropzone({
            name: 'de_file',
            // @ts-ignore
            fontSize: '16px',
            required: true,
            // @ts-ignore
            label: <FormLabel>{t('legal_documents:legal_document_properties.versions.de')}</FormLabel>,
            control,
            register,
            errors,
          })}
          <Flex justifyContent="flex-end">
            <Button type="submit" variant="primary">
              {t('legal_documents:create_legal_document.button')}
            </Button>
          </Flex>
        </Stack>
      </ContentCard>
    </Box>
  );
};

export default CreateLegalDocumentPage;
